import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaDownload,
  FaEye,
  FaHeart,
  FaRegHeart,
  FaArrowLeft,
  FaExclamationTriangle,
  FaClock,
  FaComment,
  FaPaperPlane,
  FaFilePdf,
  FaExpand,
  FaSpinner,
  FaTimes,
  FaFlag,
  FaReply,
  FaShieldAlt,
  FaRocket,
  FaUser,
  FaCalendarAlt,
  FaStar,
  FaThumbsUp,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import SilentPDFViewer from "../components/pdf/SilentPDFViewer";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { useFooter } from "../context/FooterContext";
import { buildApiUrl } from "../utils/apiConfig";

// Optimized PDF Worker Initialization
if (typeof window !== "undefined") {
  if (!window.__PDF_WORKER_INITIALIZED) {
    pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
    window.__PDF_WORKER_INITIALIZED = true;
  }
}

// Optimized PDF options
const pdfOptions = {
  cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/",
  cMapPacked: true,
  disableAutoFetch: true,
  enableXfa: false,
  standardFontDataUrl:
    "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/",
  isEvalSupported: false,
  useSystemFonts: false,
  disableFontFace: true,
  disableRange: true,
  disableStream: true,
  maxImageSize: -1,
};

const MAX_PAGES_TO_RENDER = 3;

const NoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfKey, setPdfKey] = useState(0);
  const fullscreenRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const [visiblePages, setVisiblePages] = useState(MAX_PAGES_TO_RENDER);
  const [loadingMore, setLoadingMore] = useState(false);
  const renderTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const documentRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Review states
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [deletingComment, setDeletingComment] = useState(false);

  const reportReasons = [
    { value: "spam", label: "Spam" },
    { value: "harassment", label: "Harassment" },
    { value: "offensive", label: "Offensive Language" },
    { value: "inappropriate", label: "Inappropriate Content" },
    { value: "other", label: "Other" },
  ];

  // Hide footer when component mounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
      if (documentRef.current) {
        documentRef.current = null;
      }
    };
  }, []);

  // Memoized handlers for performance
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    if (!isMountedRef.current) return;
    setNumPages(numPages);
    setPdfLoaded(true);
    setPdfError(false);
    setVisiblePages(Math.min(MAX_PAGES_TO_RENDER, numPages));
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error("PDF load error:", error);
    if (!isMountedRef.current) return;
    if (error.message && error.message.includes("Worker was terminated")) {
      setPdfKey((prev) => prev + 1);
      return;
    }
    setPdfError(true);
    toast.error("Failed to load PDF preview. Try downloading instead.");
  }, []);

  // Optimized scroll handler with throttling
  const handleScroll = useCallback(() => {
    if (!pdfContainerRef.current || !numPages) return;
    if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);
    renderTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = pdfContainerRef.current;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 400;
      if (nearBottom && visiblePages < numPages && !loadingMore) {
        setLoadingMore(true);
        setVisiblePages((prev) => Math.min(prev + 3, numPages));
        setTimeout(() => {
          if (isMountedRef.current) setLoadingMore(false);
        }, 500);
      }
    }, 150);
  }, [numPages, visiblePages, loadingMore]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement && fullscreenRef.current) {
        fullscreenRef.current.scrollTop = 0;
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    const container = pdfContainerRef.current;
    if (container) container.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (container) container.removeEventListener("scroll", handleScroll);
      if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (id) {
      fetchNote();
      fetchComments();
    }
  }, [id]);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const fetchNote = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `/api/notes/${id}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!isMountedRef.current) return;
      setNote(response.data);
      if (response.data.fileUrl) {
        let url = response.data.fileUrl;
        if (url.includes("cloudinary") && url.includes("image/upload")) {
          url = url.replace("image/upload", "raw/upload");
        }
        const finalUrl = `${url}?t=${Date.now()}`;
        setPdfUrl(finalUrl);
        setPdfLoaded(false);
        setPdfError(false);
        setPdfKey((prev) => prev + 1);
      }
      const userId = user?._id || user?.id;
      const uploadedById = response.data.uploadedBy?._id;
      const isUserAdmin = user?.role === "admin";
      const isUserModerator = user?.role === "moderator";
      setIsOwner(userId === uploadedById);
      setIsAdmin(isUserAdmin);
      setIsModerator(isUserModerator);
      if (user) setLiked(response.data.likes?.includes(userId));
    } catch (error) {
      console.error("Fetch note error:", error);
      if (!isMountedRef.current) return;
      if (error.response?.status === 403) {
        toast.error("This note is pending admin approval");
        navigate("/dashboard");
      } else if (error.response?.status === 404) {
        navigate("/notes");
      } else {
        toast.error("Failed to fetch note details");
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(
        `/api/comments/note/${id}`,
      );
      if (isMountedRef.current) setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      if (isMountedRef.current) setLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (!isAuthenticated) {
      toast.error("Please login to add a review");
      navigate("/login");
      return;
    }
    setSubmittingComment(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/comments/note/${id}`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNewComment("");
      fetchComments();
      toast.success("Review added successfully!");
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast.error("Failed to add review");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || !selectedComment) return;
    if (!isAuthenticated) {
      toast.error("Please login to reply");
      navigate("/login");
      return;
    }
    setSubmittingComment(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/comments/${selectedComment._id}/reply`,
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowReplyModal(false);
      setSelectedComment(null);
      setReplyContent("");
      fetchComments();
      toast.success("Reply added!");
    } catch (error) {
      console.error("Failed to add reply:", error);
      toast.error("Failed to add reply");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleReportComment = async () => {
    if (!reportReason) {
      toast.error("Please select a reason for reporting");
      return;
    }
    const finalReason =
      reportReason === "other" && customReason ? customReason : reportReason;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/comments/${reportingComment._id}/report`,
        { reason: finalReason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowReportModal(false);
      setReportingComment(null);
      setReportReason("");
      setCustomReason("");
      toast.success(
        "Comment reported successfully. Our moderators will review it.",
      );
    } catch (error) {
      console.error("Report error:", error);
      if (error.response?.status === 400) {
        toast.error("You have already reported this comment");
      } else {
        toast.error("Failed to report comment");
      }
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;
    setDeletingComment(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Comment deleted successfully");
      fetchComments();
    } catch (error) {
      console.error("Delete comment error:", error);
      toast.error("Failed to delete comment");
    } finally {
      setDeletingComment(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to like notes");
      navigate("/login");
      return;
    }
    if (note?.status !== "approved" && !isAdmin && !isModerator) {
      toast.error("Cannot like unapproved notes");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `/api/notes/${id}/like`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setLiked(!liked);
      setNote((prev) => ({
        ...prev,
        likes: liked
          ? prev.likes.filter((id) => id !== (user?._id || user?.id))
          : [...(prev.likes || []), user?._id || user?.id],
      }));
      toast.success(liked ? "Removed from favorites" : "Added to favorites");
    } catch (error) {
      console.error("Like error:", error);
      toast.error("Failed to like note");
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to download notes");
        navigate("/login");
        return;
      }
      const response = await axios.post(
        `/api/notes/${id}/download`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const fileUrl = response.data.downloadUrl;
      const filename = response.data.filename || "note.pdf";
      toast.loading("Downloading...", { id: "download" });
      const fileResponse = await fetch(fileUrl);
      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Download completed!", { id: "download" });
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download. Please try again.", { id: "download" });
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    }
  };

  const toggleFullscreen = () => {
    const previewElement = document.getElementById("pdf-fullscreen-container");
    if (!isFullscreen) {
      previewElement?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  const getPdfWidth = useCallback(() => {
    if (isFullscreen) return Math.min(window.innerWidth - 100, 800);
    if (isMobile) return window.innerWidth - 60;
    return 450;
  }, [isFullscreen, isMobile]);

  const getProfileImageUrl = (profilePicture, name) => {
    if (!profilePicture || imageError) {
      const initials =
        name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2) || "U";
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 50 50'%3E%3Crect width='50' height='50' fill='%238b5cf6'/%3E%3Ctext x='25' y='32' font-size='20' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
    }
    if (profilePicture?.startsWith("http")) return profilePicture;
    return buildApiUrl(profilePicture);
  };

  // Optimized PDF Renderer with useMemo
  const PdfRenderer = useMemo(() => {
    if (!pdfUrl) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <FaFilePdf size={48} color={getTextSecondary()} />
          <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
            No PDF available
          </p>
        </div>
      );
    }
    if (pdfError) {
      return (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <FaExclamationTriangle size={40} color="#f59e0b" />
          <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
            Unable to load PDF preview
          </p>
          <button
            onClick={handleDownload}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
            }}
          >
            <FaDownload /> Download Instead
          </button>
        </div>
      );
    }
    return (
      <Document
        key={pdfKey}
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={pdfOptions}
        loading={
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FaSpinner size={24} color="#8b5cf6" />
            </motion.div>
            <p style={{ color: getTextSecondary(), marginTop: "0.5rem" }}>
              Loading PDF preview...
            </p>
          </div>
        }
      >
        {pdfLoaded && numPages ? (
          <div>
            {Array.from(new Array(Math.min(visiblePages, numPages))).map(
              (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  style={{
                    marginBottom: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={getPdfWidth() * scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div style={{ padding: "0.5rem", textAlign: "center" }}>
                        Loading page {index + 1}...
                      </div>
                    }
                    error={
                      <div
                        style={{
                          padding: "0.5rem",
                          textAlign: "center",
                          color: "red",
                        }}
                      >
                        Failed to load page {index + 1}
                      </div>
                    }
                  />
                </div>
              ),
            )}
            {visiblePages < numPages && (
              <div style={{ textAlign: "center", padding: "0.5rem" }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <FaSpinner size={12} color="#8b5cf6" />
                </motion.div>
                <p style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
                  Loading more pages...
                </p>
              </div>
            )}
          </div>
        ) : pdfLoaded && !numPages ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <FaFilePdf size={48} color={getTextSecondary()} />
            <p style={{ color: getTextSecondary() }}>No pages found in PDF</p>
          </div>
        ) : null}
      </Document>
    );
  }, [
    pdfUrl,
    pdfError,
    pdfLoaded,
    numPages,
    visiblePages,
    scale,
    getPdfWidth,
    pdfKey,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    handleDownload,
    getTextSecondary,
  ]);

  if (loading) return <Loader />;
  if (!note)
    return (
      <div
        style={{ textAlign: "center", padding: "2rem", color: getTextColor() }}
      >
        Note not found
      </div>
    );

  const showPendingMessage =
    note.status !== "approved" && !isAdmin && !isModerator && !isOwner;
  const isPdf =
    note.fileType === "pdf" ||
    note.fileUrl?.includes(".pdf") ||
    note.fileUrl?.includes(".pdf?");
  const canViewPreview =
    (note.status === "approved" || isAdmin || isModerator || isOwner) && isPdf;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Link
          to="/notes"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            textDecoration: "none",
            marginBottom: "1.5rem",
            fontWeight: "500",
            padding: "0.5rem 1rem",
            borderRadius: "10px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.2)"
              : "rgba(139,92,246,0.1)";
            e.target.style.transform = "translateX(-5px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)";
            e.target.style.transform = "translateX(0)";
          }}
        >
          <FaArrowLeft /> Back to Notes
        </Link>

        {/* Desktop View */}
        {!isMobile && (
          <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
            {canViewPreview && (
              <div style={{ flex: 1.2, minWidth: "450px" }}>
                <div
                  style={{
                    background: getCardBackground(),
                    backdropFilter: "blur(20px)",
                    borderRadius: "20px",
                    border: `1px solid ${getBorderColor()}`,
                    position: "sticky",
                    top: "100px",
                    overflow: "hidden",
                    height: "650px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "#f8f9fa",
                      padding: "0.5rem 1rem",
                      borderBottom: `1px solid ${getBorderColor()}`,
                      fontSize: "0.8rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FaFilePdf color="#ef4444" size={14} />
                    <span style={{ color: getTextColor() }}>
                      Document Preview
                    </span>
                  </div>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <SilentPDFViewer fileUrl={pdfUrl} filename={note.title} />
                  </div>
                </div>
              </div>
            )}

            <div style={{ flex: canViewPreview ? 1.5 : 2, minWidth: "350px" }}>
              <NoteDetailsCard
                note={note}
                showPendingMessage={showPendingMessage}
                isAdmin={isAdmin}
                isModerator={isModerator}
                isOwner={isOwner}
                liked={liked}
                handleLike={handleLike}
                handleDownload={handleDownload}
                getProfileImageUrl={getProfileImageUrl}
                imageError={imageError}
                setImageError={setImageError}
                comments={comments}
                loadingComments={loadingComments}
                newComment={newComment}
                setNewComment={setNewComment}
                handleSubmitComment={handleSubmitComment}
                handleReportComment={handleReportComment}
                handleSubmitReply={handleSubmitReply}
                handleDeleteComment={handleDeleteComment}
                replyContent={replyContent}
                setReplyContent={setReplyContent}
                submittingComment={submittingComment}
                deletingComment={deletingComment}
                showReplyModal={showReplyModal}
                setShowReplyModal={setShowReplyModal}
                selectedComment={selectedComment}
                setSelectedComment={setSelectedComment}
                showReportModal={showReportModal}
                setShowReportModal={setShowReportModal}
                reportingComment={reportingComment}
                setReportingComment={setReportingComment}
                reportReason={reportReason}
                setReportReason={setReportReason}
                customReason={customReason}
                setCustomReason={setCustomReason}
                reportReasons={reportReasons}
                isAuthenticated={isAuthenticated}
                user={user}
                isMobile={isMobile}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        )}

        {/* Mobile View */}
        {isMobile && (
          <div>
            {canViewPreview && (
              <div style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    background: getCardBackground(),
                    backdropFilter: "blur(20px)",
                    borderRadius: "16px",
                    border: `1px solid ${getBorderColor()}`,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "#f8f9fa",
                      padding: "0.5rem",
                      borderBottom: `1px solid ${getBorderColor()}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "0.3rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaFilePdf color="#ef4444" size={14} />
                      <span
                        style={{
                          fontWeight: "500",
                          fontSize: "0.8rem",
                          color: getTextColor(),
                        }}
                      >
                        Preview
                      </span>
                      {numPages && (
                        <span
                          style={{
                            fontSize: "0.6rem",
                            color: getTextSecondary(),
                          }}
                        >
                          {numPages} pg
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "0.3rem" }}>
                      <button
                        onClick={zoomOut}
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.2)"
                            : "#f0f0f0",
                          border: "none",
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          color: getTextColor(),
                        }}
                      >
                        -
                      </button>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          padding: "0 0.2rem",
                          color: getTextColor(),
                        }}
                      >
                        {Math.round(scale * 100)}%
                      </span>
                      <button
                        onClick={zoomIn}
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.2)"
                            : "#f0f0f0",
                          border: "none",
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          color: getTextColor(),
                        }}
                      >
                        +
                      </button>
                      <button
                        onClick={toggleFullscreen}
                        style={{
                          background:
                            "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                          border: "none",
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          color: "white",
                        }}
                      >
                        <FaExpand size={10} />
                      </button>
                    </div>
                  </div>
                  <div
                    ref={pdfContainerRef}
                    id="pdf-fullscreen-container"
                    style={{
                      maxHeight: "400px",
                      overflow: "auto",
                      background: isDarkMode ? "#1a1a2e" : "#f0f0f0",
                      padding: "0.5rem",
                    }}
                  >
                    {PdfRenderer}
                  </div>
                </div>
              </div>
            )}

            <NoteDetailsCard
              note={note}
              showPendingMessage={showPendingMessage}
              isAdmin={isAdmin}
              isModerator={isModerator}
              isOwner={isOwner}
              liked={liked}
              handleLike={handleLike}
              handleDownload={handleDownload}
              getProfileImageUrl={getProfileImageUrl}
              imageError={imageError}
              setImageError={setImageError}
              comments={comments}
              loadingComments={loadingComments}
              newComment={newComment}
              setNewComment={setNewComment}
              handleSubmitComment={handleSubmitComment}
              handleReportComment={handleReportComment}
              handleSubmitReply={handleSubmitReply}
              handleDeleteComment={handleDeleteComment}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              submittingComment={submittingComment}
              deletingComment={deletingComment}
              showReplyModal={showReplyModal}
              setShowReplyModal={setShowReplyModal}
              selectedComment={selectedComment}
              setSelectedComment={setSelectedComment}
              showReportModal={showReportModal}
              setShowReportModal={setShowReportModal}
              reportingComment={reportingComment}
              setReportingComment={setReportingComment}
              reportReason={reportReason}
              setReportReason={setReportReason}
              customReason={customReason}
              setCustomReason={setCustomReason}
              reportReasons={reportReasons}
              isAuthenticated={isAuthenticated}
              user={user}
              isMobile={isMobile}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .react-pdf__Page {
          background: white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
          margin-bottom: 0.8rem;
        }
        .react-pdf__Page:last-child { margin-bottom: 0; }
        .react-pdf__Page canvas { max-width: 100%; height: auto !important; }
        @media (max-width: 768px) { .react-pdf__Page { box-shadow: none; } }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(135deg, #8b5cf6, #3b82f6); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(135deg, #7c3aed, #2563eb); }
      `}</style>
    </div>
  );
};

// Note Details Card Component - Updated with Modern Design
const NoteDetailsCard = ({
  note,
  showPendingMessage,
  isAdmin,
  isModerator,
  isOwner,
  liked,
  handleLike,
  handleDownload,
  getProfileImageUrl,
  imageError,
  setImageError,
  comments,
  loadingComments,
  newComment,
  setNewComment,
  handleSubmitComment,
  handleReportComment,
  handleSubmitReply,
  handleDeleteComment,
  replyContent,
  setReplyContent,
  submittingComment,
  deletingComment,
  showReplyModal,
  setShowReplyModal,
  selectedComment,
  setSelectedComment,
  showReportModal,
  setShowReportModal,
  reportingComment,
  setReportingComment,
  reportReason,
  setReportReason,
  customReason,
  setCustomReason,
  reportReasons,
  isAuthenticated,
  user,
  isMobile,
  isDarkMode,
}) => {
  const canModerate = isAdmin || isModerator;
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "24px",
          padding: isMobile ? "1rem" : "2rem",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        {showPendingMessage && (
          <div
            style={{
              background: "rgba(245,158,11,0.1)",
              border: "1px solid #f59e0b",
              color: "#f59e0b",
              padding: "0.8rem",
              borderRadius: "12px",
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FaClock />{" "}
            <span>
              <strong>Pending Approval:</strong> This note is waiting for admin
              review.
            </span>
          </div>
        )}
        {note.status !== "approved" && (isAdmin || isModerator || isOwner) && (
          <div
            style={{
              background: "rgba(59,130,246,0.1)",
              border: "1px solid #3b82f6",
              color: "#3b82f6",
              padding: "0.8rem",
              borderRadius: "12px",
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <FaClock />{" "}
            <span>
              <strong>Admin/Moderator/Author View:</strong> This note is
              currently {note.status}.{" "}
              {(isAdmin || isModerator) &&
                "You can review it from the Admin Dashboard."}
            </span>
          </div>
        )}

        <h1
          style={{
            fontSize: isMobile ? "1.5rem" : "2rem",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "1rem",
          }}
        >
          {note.title}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            marginBottom: "1rem",
          }}
        >
          <span
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              padding: "0.2rem 0.8rem",
              borderRadius: "20px",
              fontSize: "0.8rem",
            }}
          >
            {note.subjectCode}
          </span>
          <span
            style={{
              background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f0f0f0",
              color: getTextSecondary(),
              padding: "0.2rem 0.8rem",
              borderRadius: "20px",
              fontSize: "0.8rem",
            }}
          >
            Sem {note.semester}
          </span>
          {note.module && (
            <span
              style={{
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f0f0f0",
                color: getTextSecondary(),
                padding: "0.2rem 0.8rem",
                borderRadius: "20px",
                fontSize: "0.8rem",
              }}
            >
              Module {note.module}
            </span>
          )}
          <span
            style={{
              background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f0f0f0",
              color: getTextSecondary(),
              padding: "0.2rem 0.8rem",
              borderRadius: "20px",
              fontSize: "0.8rem",
            }}
          >
            {note.branch}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            padding: "0.8rem 0",
            borderTop: `1px solid ${getBorderColor()}`,
            borderBottom: `1px solid ${getBorderColor()}`,
            marginBottom: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              color: getTextSecondary(),
              fontSize: "0.9rem",
            }}
          >
            <FaEye /> {note.views || 0}
          </span>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              color: getTextSecondary(),
              fontSize: "0.9rem",
            }}
          >
            <FaDownload /> {note.downloads || 0}
          </span>
          <button
            onClick={handleLike}
            disabled={note.status !== "approved" && !isAdmin && !isModerator}
            style={{
              background: "none",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              color: liked ? "#ef4444" : getTextSecondary(),
              cursor:
                note.status === "approved" || isAdmin || isModerator
                  ? "pointer"
                  : "not-allowed",
              fontSize: "0.9rem",
              opacity:
                note.status !== "approved" && !isAdmin && !isModerator
                  ? 0.6
                  : 1,
            }}
          >
            {liked ? <FaHeart /> : <FaRegHeart />} {note.likes?.length || 0}
          </button>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <h3
            style={{
              color: getTextColor(),
              marginBottom: "0.5rem",
              fontSize: isMobile ? "1.1rem" : "1.2rem",
              fontWeight: "bold",
            }}
          >
            Description
          </h3>
          <p
            style={{
              color: getTextSecondary(),
              lineHeight: "1.6",
              fontSize: isMobile ? "0.9rem" : "1rem",
            }}
          >
            {note.description}
          </p>
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <h3
            style={{
              color: getTextColor(),
              marginBottom: "0.5rem",
              fontSize: isMobile ? "1.1rem" : "1.2rem",
              fontWeight: "bold",
            }}
          >
            Uploaded By
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.8rem",
              color: getTextColor(),
            }}
          >
            <img
              src={getProfileImageUrl(
                note.uploadedBy?.profilePicture,
                note.uploadedBy?.name,
              )}
              alt={note.uploadedBy?.name}
              onError={() => setImageError(true)}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #8b5cf6",
              }}
            />
            <div>
              <h4
                style={{
                  color: getTextColor(),
                  marginBottom: "0.2rem",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                }}
              >
                {note.uploadedBy?.name}
              </h4>
              <p style={{ color: getTextSecondary(), fontSize: "0.8rem" }}>
                {note.uploadedBy?.contributions?.notesUploaded || 0} notes
                uploaded
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleDownload}
          disabled={note.status !== "approved" && !isAdmin && !isModerator}
          style={{
            width: "100%",
            padding: "0.8rem",
            background:
              note.status !== "approved" && !isAdmin && !isModerator
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            fontSize: isMobile ? "0.9rem" : "1rem",
            fontWeight: "600",
            cursor:
              note.status === "approved" || isAdmin || isModerator
                ? "pointer"
                : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            marginBottom: "1.5rem",
            boxShadow:
              note.status !== "approved" && !isAdmin && !isModerator
                ? "none"
                : "0 4px 14px 0 rgba(139,92,246,0.4)",
          }}
        >
          <FaDownload />{" "}
          {note.status === "approved"
            ? "Download Note"
            : isAdmin || isModerator
              ? "Download Note (Preview)"
              : isOwner
                ? "Pending Approval"
                : "Not Available"}
        </button>

        {(isAdmin || isModerator) && note.status === "pending" && (
          <div style={{ marginTop: "0.5rem", textAlign: "center" }}>
            <Link
              to="/admin"
              style={{
                color: "#8b5cf6",
                textDecoration: "none",
                fontSize: "0.85rem",
              }}
            >
              ⚡ Go to Admin Dashboard to approve this note
            </Link>
          </div>
        )}

        {/* Comments Section */}
        <div style={{ marginTop: "1.5rem" }}>
          <h3
            style={{
              color: getTextColor(),
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: isMobile ? "1.1rem" : "1.2rem",
            }}
          >
            <FaComment /> Reviews ({comments.length})
          </h3>

          {isAuthenticated && (
            <form
              onSubmit={handleSubmitComment}
              style={{ marginBottom: "1.5rem" }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "0.8rem",
                  alignItems: "flex-start",
                }}
              >
                <img
                  src={
                    user?.profilePicture ||
                    `https://ui-avatars.com/api/?name=${user?.name}&background=8b5cf6&color=fff&size=35`
                  }
                  alt={user?.name}
                  style={{
                    width: "35px",
                    height: "35px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write your review or comment..."
                    rows="2"
                    style={{
                      width: "100%",
                      padding: "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      outline: "none",
                      fontSize: "0.9rem",
                      resize: "vertical",
                      fontFamily: "inherit",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "0.3rem",
                    }}
                  >
                    <button
                      type="submit"
                      disabled={submittingComment || !newComment.trim()}
                      style={{
                        padding: "0.4rem 1rem",
                        background: !newComment.trim()
                          ? "#94a3b8"
                          : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: !newComment.trim() ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                        fontSize: "0.8rem",
                        fontWeight: "500",
                      }}
                    >
                      <FaPaperPlane size={12} />{" "}
                      {submittingComment ? "Posting..." : "Post"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {loadingComments ? (
            <div style={{ textAlign: "center", padding: "1rem" }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <FaSpinner size={20} color="#8b5cf6" />
              </motion.div>
            </div>
          ) : comments.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "1rem",
                background: isDarkMode ? "rgba(139,92,246,0.05)" : "#f8f9fa",
                borderRadius: "12px",
              }}
            >
              <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                No reviews yet.
              </p>
              {isAuthenticated && (
                <p style={{ color: getTextSecondary(), fontSize: "0.75rem" }}>
                  Be the first to review!
                </p>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "1rem" }}>
              {comments.map((comment) => (
                <div
                  key={comment._id}
                  style={{
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "16px",
                    borderLeft: `3px solid #8b5cf6`,
                    border: `1px solid ${getBorderColor()}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <img
                      src={
                        comment.user?.profilePicture ||
                        `https://ui-avatars.com/api/?name=${comment.user?.name}&background=8b5cf6&color=fff&size=35`
                      }
                      alt={comment.user?.name}
                      style={{
                        width: "35px",
                        height: "35px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <div>
                      <strong style={{ color: getTextColor() }}>
                        {comment.user?.name}
                      </strong>
                      <span
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.7rem",
                          marginLeft: "0.5rem",
                        }}
                      >
                        {new Date(comment.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                  <p
                    style={{
                      color: getTextColor(),
                      marginLeft: "3rem",
                      lineHeight: "1.5",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {comment.content}
                  </p>
                  <div
                    style={{
                      marginLeft: "3rem",
                      marginTop: "0.5rem",
                      display: "flex",
                      gap: "1rem",
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedComment(comment);
                        setReplyContent("");
                        setShowReplyModal(true);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#8b5cf6",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaReply size={12} /> Reply
                    </button>
                    <button
                      onClick={() => {
                        setReportingComment(comment);
                        setReportReason("");
                        setCustomReason("");
                        setShowReportModal(true);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#ef4444",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaFlag size={12} /> Report
                    </button>
                    {canModerate && (
                      <button
                        onClick={() => handleDeleteComment(comment._id)}
                        disabled={deletingComment}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        <FaTimes size={12} /> Delete
                      </button>
                    )}
                  </div>
                  {comment.replies && comment.replies.length > 0 && (
                    <div
                      style={{
                        marginLeft: "3rem",
                        marginTop: "1rem",
                        paddingLeft: "1rem",
                        borderLeft: `2px solid ${getBorderColor()}`,
                      }}
                    >
                      {comment.replies.map((reply) => (
                        <div
                          key={reply._id}
                          style={{
                            background: getCardBackground(),
                            padding: "0.8rem",
                            borderRadius: "12px",
                            marginBottom: "0.5rem",
                            border: `1px solid ${getBorderColor()}`,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.8rem",
                              marginBottom: "0.3rem",
                            }}
                          >
                            <img
                              src={
                                reply.user?.profilePicture ||
                                `https://ui-avatars.com/api/?name=${reply.user?.name}&background=8b5cf6&color=fff&size=25`
                              }
                              alt={reply.user?.name}
                              style={{
                                width: "25px",
                                height: "25px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <div>
                              <strong
                                style={{
                                  color: getTextColor(),
                                  fontSize: "0.8rem",
                                }}
                              >
                                {reply.user?.name}
                              </strong>
                              <span
                                style={{
                                  color: getTextSecondary(),
                                  fontSize: "0.6rem",
                                  marginLeft: "0.5rem",
                                }}
                              >
                                {new Date(reply.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p
                            style={{
                              color: getTextSecondary(),
                              fontSize: "0.8rem",
                              marginLeft: "2rem",
                            }}
                          >
                            {reply.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Reply Modal */}
      {showReplyModal && selectedComment && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowReplyModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              padding: "2rem",
              borderRadius: "24px",
              maxWidth: "500px",
              width: "90%",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "1rem", color: getTextColor() }}>
              Reply to {selectedComment.user?.name}
            </h3>
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write your reply..."
              rows="3"
              style={{
                width: "100%",
                padding: "0.8rem",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "12px",
                marginBottom: "1rem",
                fontSize: "1rem",
                resize: "vertical",
                fontFamily: "inherit",
                background: getInputBackground(),
                color: getTextColor(),
              }}
              autoFocus
            />
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowReplyModal(false)}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || submittingComment}
                style={{
                  padding: "0.5rem 1rem",
                  background: !replyContent.trim()
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: !replyContent.trim() ? "not-allowed" : "pointer",
                }}
              >
                {submittingComment ? "Posting..." : "Post Reply"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && reportingComment && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowReportModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              padding: "2rem",
              borderRadius: "24px",
              maxWidth: "500px",
              width: "90%",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: "1rem", color: getTextColor() }}>
              Report Comment
            </h3>
            <p
              style={{
                color: getTextSecondary(),
                marginBottom: "1rem",
                fontSize: "0.9rem",
              }}
            >
              Why are you reporting this comment?
            </p>
            <select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              style={{
                width: "100%",
                padding: "0.8rem",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "12px",
                marginBottom: "1rem",
                fontSize: "1rem",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              <option value="">Select a reason</option>
              {reportReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
            {reportReason === "other" && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Please provide details..."
                rows="2"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  marginBottom: "1rem",
                  fontSize: "0.9rem",
                  resize: "vertical",
                  fontFamily: "inherit",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReportComment}
                disabled={!reportReason}
                style={{
                  padding: "0.5rem 1rem",
                  background: !reportReason
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: !reportReason ? "not-allowed" : "pointer",
                }}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NoteDetail;

